import styled from "styled-components";
import {
  ButtonLink,
  ButtonRound,
  Column,
  ContentBox,
  ContentPage,
  DatePicker,
  ExportIcon,
  Row,
  SelectorPageStatus,
  Skeleton,
  StatePill,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import { schedulePeriod, formatPeriodLabel } from "@helpers/competencePeriod";
import { fixedAdjustmentCompetence, isCompetenceOpen } from "@helpers/fixedAdjustmentCompetence";
import { DateText, DomainStatus, Money, escapeHtml, formatCompetencia, formatHours } from "@components/ow";
import { isFixedWithAdjustment } from "../ContractDetail";

// Pagamentos (cronogramas), no que a visão da empresa (/c/payment-schedules) e a do
// prestador (/e/payment-schedules) compartilham: status, filtros, células da lista e
// blocos do detalhe.

export const SCHEDULE_STATUS = { PENDING: "pendente", PAID: "pago", OVERDUE: "atrasado", CANCELLED: "cancelado" };
export const INVOICE_STATUS = { PENDING: "pendente", APPROVED: "aprovada", REJECTED: "recusada", PAID: "paga" };

// Pílulas do filtro de status: uma por vez, "Todos" sem filtro (as mesmas da aba Pagamentos)
export const STATUS_DATA = [
  { label: "Todos", value: "" },
  { label: "Pendente", value: "PENDING" },
  { label: "Pago", value: "PAID" },
  { label: "Atrasado", value: "OVERDUE" },
  { label: "Cancelado", value: "CANCELLED" },
];

export const STATUS_LABELS = { PENDING: "Pendente", PAID: "Pago", OVERDUE: "Atrasado", CANCELLED: "Cancelado" };

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => p.$align || "stretch"};
  gap: ${(p) => p.$gap || "16px"};
`;

export const Inline = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const Filters = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
`;

const FilterFields = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
`;

const CompetenceField = styled.div`
  width: 220px;
`;

export const competenceOf = (schedule) => ({ month: Number(schedule.reference_month), year: Number(schedule.reference_year) });

/**
 * Saldo de horas da competência aberta no valor fixo com ajuste, sem decimal
 * ("Faltam 72h", "8h30 a mais"). O `balanceHoursLabel` do helper usa decimal e
 * continua nas telas antigas.
 */
export function balanceLabel(balanceHours) {
  const balance = Number(balanceHours) || 0;
  const text = formatHours(Math.round(Math.abs(balance) * 60));
  if (balance < 0) return `Faltam ${text}`;
  if (balance > 0) return `${text} a mais`;
  return "Horas em dia";
}

/**
 * Linha de filtros das listas: pílulas de status à esquerda; "Limpar filtros" e a
 * competência (vazia = todas) à direita.
 */
export function PaymentFilters({ id, status, onStatusChange, competence, onCompetenceChange, onClear }) {
  const hasFilter = !!status.value || !!competence;
  return (
    <Filters>
      <SelectorPageStatus options={STATUS_DATA} selected={status} onChange={onStatusChange} />
      <FilterFields>
        {hasFilter ? <ButtonLink $text="Limpar filtros" $action={onClear} $noWidth /> : null}
        <CompetenceField>
          <DatePicker
            $idForm={id}
            $label="Competência"
            $variant="default"
            $isCompetence
            $isClearable
            $popperPlacement="bottom-end"
            $date={competence ? new Date(competence.year, competence.month - 1, 1) : null}
            $setDate={(date) => onCompetenceChange(date ? { month: date.getMonth() + 1, year: date.getFullYear() } : null)}
          />
        </CompetenceField>
      </FilterFields>
    </Filters>
  );
}

/** Dois textos empilhados: o principal e a legenda em N400. */
export function TwoLines({ main, sub, bold }) {
  return (
    <Stack $gap="2px">
      <Text $text={main} $variant={bold ? "paragraph_small_bold" : "paragraph_small_medium"} $color="N900" />
      {sub ? <Text $text={sub} $variant="caption_medium" $color="N400" /> : null}
    </Stack>
  );
}

/** Célula da competência: "Out/2026" e a janela de apuração, quando houver. */
export function CompetenceCell({ schedule }) {
  const period = schedulePeriod(schedule);
  return (
    <TwoLines
      main={formatCompetencia(competenceOf(schedule))}
      sub={period ? `apura ${formatPeriodLabel(period.start, period.end)}` : null}
    />
  );
}

/** Célula da nota fiscal: status da nota, "Sem nota" no pendente ou "-". */
export function InvoiceStatusCell({ schedule }) {
  const nf = schedule.taxInvoice;
  if (nf) return <DomainStatus domain="notaFiscal" value={INVOICE_STATUS[nf.status] || nf.status} />;
  // Competência encerrada não recebe nota: não há o que cobrar
  if (schedule.status !== "PENDING") return "-";
  return <Text $text="Sem nota" $variant="paragraph_small_medium" $color="N400" />;
}

/** Arquivo da nota: nome que baixa, status e o botão de baixar (sem permissão, só o nome). */
export function InvoiceFile({ invoice, onDownload }) {
  const name = invoice.filename || "Nota fiscal";
  return (
    <Inline>
      {onDownload ? (
        <ButtonLink $text={name} $action={onDownload} $noPadding $noWidth />
      ) : (
        <Text $text={escapeHtml(name)} $variant="paragraph_small_medium" $color="N900" />
      )}
      <DomainStatus domain="notaFiscal" value={INVOICE_STATUS[invoice.status] || invoice.status} />
      {onDownload ? (
        <ButtonRound
          title="Baixar nota fiscal"
          aria-label="Baixar nota fiscal"
          $variant="outline"
          $icon={<ExportIcon />}
          $noWidth
          $action={onDownload}
        />
      ) : null}
    </Inline>
  );
}

/** Itens do bloco "Pagamento" do detalhe. */
export function paymentItems(schedule, period) {
  const competence = fixedAdjustmentCompetence(schedule, period);
  const hasValue = schedule.expected_value != null && schedule.expected_value !== "";
  const items = [
    { label: "Status", value: <DomainStatus domain="pagamento" value={SCHEDULE_STATUS[schedule.status] || schedule.status} /> },
    {
      label: "Competência",
      value: (
        <TwoLines
          main={formatCompetencia(competenceOf(schedule))}
          sub={period ? `apura ${formatPeriodLabel(period.start, period.end)}` : null}
        />
      ),
    },
    { label: "Vencimento", value: schedule.due_date ? <DateText value={schedule.due_date} /> : null },
    {
      label: "Valor esperado",
      value: hasValue ? (
        <Inline>
          <Money value={schedule.expected_value} />
          {isCompetenceOpen(schedule) ? <StatePill $text="Parcial" $variant="orange" $size="small" /> : null}
          {competence?.isFrozen ? <StatePill $text="Encerrada" $variant="gray" $size="small" /> : null}
        </Inline>
      ) : null,
    },
    { label: "Valor pago", value: Number(schedule.actual_value) ? <Money value={schedule.actual_value} /> : null },
  ];
  if (schedule.notes) items.push({ label: "Observações", value: schedule.notes, span: 2 });
  return items;
}

/** Valores do contrato no bloco "Contrato" do detalhe (depois da remuneração). */
export function contractValueItems(contract) {
  const items = [];
  if (contract.remuneration_type === "FIXED") {
    items.push({ label: "Valor mensal", value: <Money value={contract.fixed_value} /> });
  }
  if (isFixedWithAdjustment(contract)) {
    items.push({ label: "Valor fixo mensal", value: <Money value={contract.fixed_value} /> });
  }
  if (contract.remuneration_type === "HOURLY") {
    items.push({ label: "Valor por hora", value: <Money value={contract.hourly_rate} /> });
  }
  return items;
}

/** Itens do bloco "Histórico" do detalhe. */
export function historyItems(schedule) {
  // updated_at muda a cada alteração posterior do cronograma: só como reserva
  const paidAt = schedule.status === "PAID" ? schedule.payment_date || schedule.updated_at : null;
  const items = [{ label: "Criado em", value: schedule.created_at ? <DateText value={schedule.created_at} /> : null }];
  if (paidAt) items.push({ label: "Pago em", value: <DateText value={paidAt} /> });
  return items;
}

export function PaymentDetailSkeleton() {
  return (
    <ContentPage aria-busy="true">
      <ContentBox>
        <Row $gap={20} $alignItems="flex-start">
          <Column $columns={6}>
            <Skeleton $height={260} />
          </Column>
          <Column $columns={6}>
            <Stack $gap="20px">
              <Skeleton $height={140} />
              <Skeleton $height={160} />
            </Stack>
          </Column>
        </Row>
      </ContentBox>
    </ContentPage>
  );
}
